/**
 * StepCloud.tsx — Connect cloud infrastructure step.
 *
 * Four provider tabs: AWS | GCP | Azure | SSH
 * Each shows a plain-English setup guide + credentials form + Test Connection.
 */

import React, { useRef, useState } from 'react';
import { AWS_REGIONS } from '../../../config/awsRegions';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorIcon from '@mui/icons-material/Error';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import axios from 'axios';
import API_BASE from '../../../config/api';

const PROVIDERS = ['aws', 'gcp', 'azure', 'ssh'] as const;
type Provider = typeof PROVIDERS[number];

interface TestResult { success: boolean; message: string; }

interface Props {
  sessionId: string;
  onNext: () => void;
}

// ── Copy-button helper ────────────────────────────────────────────────────────
function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

// ── Numbered guide step ───────────────────────────────────────────────────────
function GuideStep({ n, text, copy }: { n: number; text: string; copy?: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
      <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: 'primary.main',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2 }}>
        <Typography variant="caption" fontWeight={700} sx={{ color: 'primary.contrastText' }}>{n}</Typography>
      </Box>
      <Typography variant="body2" sx={{ flex: 1 }}>{text}</Typography>
      {copy && <CopyBtn text={copy} />}
    </Box>
  );
}

// ── Connection status badge ───────────────────────────────────────────────────
function StatusBadge({ result }: { result: TestResult | null }) {
  if (!result) return null;
  return (
    <Alert
      severity={result.success ? 'success' : 'error'}
      icon={result.success ? <CheckCircleIcon /> : <ErrorIcon />}
      sx={{ mt: 2, borderRadius: 2 }}
    >
      {result.message}
    </Alert>
  );
}

// ── AWS Form ──────────────────────────────────────────────────────────────────
function AWSForm({ sessionId, onConnected }: { sessionId: string; onConnected: (accountId: string) => void }) {
  const [step, setStep] = useState<'init' | 'waiting' | 'confirm'>('init');
  const [loading, setLoading] = useState(false);
  const [externalId, setExternalId] = useState('');
  const [shieldAccountId, setShieldAccountId] = useState('');
  const [stackName, setStackName] = useState('');
  const [launchStackUrl, setLaunchStackUrl] = useState('');
  const [roleArn, setRoleArn] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function handleLaunchStack() {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API_BASE}/onboarding/${sessionId}/aws/init-connection`, {
        purpose: 'scan',
      });
      setExternalId(data.external_id);
      setShieldAccountId(data.shield_account_id);
      setStackName(data.stack_name);
      setLaunchStackUrl(data.launch_stack_url || '');
      setStep('waiting');
    } catch (e: any) {
      setError(e.response?.data?.detail ?? 'Failed to initialize AWS connection.');
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm() {
    if (!roleArn.startsWith('arn:aws:iam::')) {
      setError('Please enter a valid IAM Role ARN (starts with arn:aws:iam::).');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API_BASE}/onboarding/${sessionId}/aws/confirm-connection`, {
        role_arn: roleArn,
        purpose: 'scan',
      });
      if (data.success) {
        setSuccess(data.message);
        setStep('confirm');
        onConnected(data.account_id || '');
      } else {
        setError(data.message || 'Role verification failed.');
      }
    } catch (e: any) {
      setError(e.response?.data?.detail ?? 'Failed to verify the role ARN.');
    } finally {
      setLoading(false);
    }
  }

  if (step === 'init') {
    return (
      <Box>
        <Box sx={{ bgcolor: 'rgba(0,180,216,0.05)', borderRadius: 2, p: 2, mb: 2 }}>
          <Typography variant="body2" fontWeight={600} mb={1}>How it works:</Typography>
          <GuideStep n={1} text="Click 'Launch Stack' to open AWS CloudFormation in a new tab" />
          <GuideStep n={2} text="Review the stack — it creates a read-only role with SecurityAudit access" />
          <GuideStep n={3} text="Click 'Create stack' in AWS Console" />
          <GuideStep n={4} text="Copy the Role ARN from the Outputs tab and paste it below" />
        </Box>
        <Button
          variant="contained"
          fullWidth
          onClick={handleLaunchStack}
          disabled={loading}
          sx={{ fontWeight: 700, py: 1.5 }}
        >
          {loading ? 'Preparing…' : 'Launch Stack in AWS Console'}
        </Button>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        A CloudFormation stack is being created in your AWS account. Once the stack completes,
        copy the <strong>Role ARN</strong> from the Outputs tab and paste it below.
      </Alert>

      <Box sx={{ bgcolor: 'rgba(0,180,216,0.05)', borderRadius: 2, p: 2, mb: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Stack Name</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontFamily="monospace">{stackName}</Typography>
          <CopyBtn text={stackName} />
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" mt={1} mb={0.5}>ExternalId (pre-filled in stack)</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>{externalId}</Typography>
          <CopyBtn text={externalId} />
        </Box>
      </Box>

      <Button
        component="a"
        href={launchStackUrl}
        target="_blank"
        rel="noopener noreferrer"
        variant="contained"
        color="primary"
        fullWidth
        startIcon={<OpenInNewIcon />}
        sx={{ fontWeight: 700, py: 1.5, mb: 2 }}
      >
        Launch Stack in AWS Console
      </Button>

      <Stack spacing={2}>
        <TextField
          label="Role ARN (from CloudFormation Outputs)"
          fullWidth
          value={roleArn}
          onChange={(e) => setRoleArn(e.target.value)}
          placeholder="arn:aws:iam::123456789012:role/shield-scan-role"
        />
        <Button
          variant="contained"
          disabled={!roleArn || loading}
          onClick={handleConfirm}
          sx={{ fontWeight: 700 }}
        >
          {loading ? 'Verifying…' : 'Verify Connection'}
        </Button>
      </Stack>

      {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </Box>
  );
}

// ── GCP Form ──────────────────────────────────────────────────────────────────
function GCPForm({ onTest }: { onTest: (creds: Record<string, string>) => Promise<void> }) {
  const [projectId, setProjectId] = useState('');
  const [keyJson, setKeyJson]     = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try { const parsed = JSON.parse(ev.target?.result as string); setKeyJson(JSON.stringify(parsed)); if (!projectId) setProjectId(parsed.project_id || ''); }
      catch { alert('Invalid JSON key file'); }
    };
    reader.readAsText(file);
  }

  return (
    <Box>
      <Box sx={{ bgcolor: 'rgba(0,180,216,0.05)', borderRadius: 2, p: 2, mb: 2 }}>
        <Typography variant="body2" fontWeight={600} mb={1}>Setup steps:</Typography>
        <GuideStep n={1} text="Go to Google Cloud Console → IAM & Admin → Service Accounts" />
        <GuideStep n={2} text='Create a service account named "shield-scanner"' copy="shield-scanner" />
        <GuideStep n={3} text="Grant the role: Security Reviewer (read-only)" copy="roles/iam.securityReviewer" />
        <GuideStep n={4} text="Create a JSON key and upload it below" />
      </Box>
      <Stack spacing={2}>
        <TextField label="Project ID" fullWidth value={projectId} onChange={(e) => setProjectId(e.target.value)} placeholder="my-project-123" />
        <Box>
          <input ref={fileRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleFile} />
          <Button variant="outlined" fullWidth onClick={() => fileRef.current?.click()} sx={{ height: 56 }}>
            {keyJson ? '✅ JSON key loaded' : 'Upload service account JSON key'}
          </Button>
        </Box>
      </Stack>
      <Button variant="outlined" sx={{ mt: 2 }} disabled={!projectId || !keyJson}
        onClick={() => onTest({ project_id: projectId, key_json: keyJson })}>
        Test Connection
      </Button>
    </Box>
  );
}

// ── Azure Form ────────────────────────────────────────────────────────────────
function AzureForm({ onTest }: { onTest: (creds: Record<string, string>) => Promise<void> }) {
  const [tenantId, setTenantId]   = useState(''); const [clientId, setClientId] = useState('');
  const [secret, setSecret]       = useState(''); const [subId, setSubId]       = useState('');
  const [show, setShow]           = useState(false);
  return (
    <Box>
      <Box sx={{ bgcolor: 'rgba(0,180,216,0.05)', borderRadius: 2, p: 2, mb: 2 }}>
        <Typography variant="body2" fontWeight={600} mb={1}>Setup steps:</Typography>
        <GuideStep n={1} text="Go to Azure Active Directory → App registrations → New registration" />
        <GuideStep n={2} text='Name the app "Shield Scanner"' copy="Shield Scanner" />
        <GuideStep n={3} text="Go to Subscriptions → Access Control → Add role: Security Reader" />
        <GuideStep n={4} text="Create a client secret and paste below" />
      </Box>
      <Stack spacing={2}>
        <TextField label="Tenant ID" fullWidth value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
        <TextField label="Client ID (App ID)" fullWidth value={clientId} onChange={(e) => setClientId(e.target.value)} />
        <TextField label="Client Secret" fullWidth type={show ? 'text' : 'password'} value={secret} onChange={(e) => setSecret(e.target.value)}
          InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShow((p) => !p)}>{show ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
        <TextField label="Subscription ID" fullWidth value={subId} onChange={(e) => setSubId(e.target.value)} />
      </Stack>
      <Button variant="outlined" sx={{ mt: 2 }} disabled={!tenantId || !clientId || !secret || !subId}
        onClick={() => onTest({ tenant_id: tenantId, client_id: clientId, client_secret: secret, subscription_id: subId })}>
        Test Connection
      </Button>
    </Box>
  );
}

// ── SSH Form ──────────────────────────────────────────────────────────────────
function SSHForm({ onTest }: { onTest: (creds: Record<string, string>) => Promise<void> }) {
  const [host, setHost]       = useState(''); const [port, setPort]   = useState('22');
  const [user, setUser]       = useState(''); const [key, setKey]     = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setKey(ev.target?.result as string);
    reader.readAsText(file);
  }

  return (
    <Box>
      <Box sx={{ bgcolor: 'rgba(0,180,216,0.05)', borderRadius: 2, p: 2, mb: 2 }}>
        <Typography variant="body2" fontWeight={600} mb={1}>How it works:</Typography>
        <Typography variant="body2" color="text.secondary">
          Shield connects to your server via SSH using a read-only key. We run security checks
          without making any changes to your system.
        </Typography>
      </Box>
      <Stack spacing={2}>
        <TextField label="Server IP or hostname" fullWidth value={host} onChange={(e) => setHost(e.target.value)} placeholder="192.168.1.100 or server.example.com" />
        <TextField label="SSH Port" fullWidth value={port} onChange={(e) => setPort(e.target.value)} type="number" />
        <TextField label="Username" fullWidth value={user} onChange={(e) => setUser(e.target.value)} placeholder="ec2-user, ubuntu, root…" />
        <Box>
          <input ref={fileRef} type="file" accept=".pem,.key" style={{ display: 'none' }} onChange={handleFile} />
          <Button variant="outlined" fullWidth onClick={() => fileRef.current?.click()} sx={{ height: 56, mb: 1 }}>
            {key ? '✅ SSH key loaded' : 'Upload SSH private key (.pem / .key)'}
          </Button>
          <Typography variant="caption" color="text.secondary">or paste your private key:</Typography>
          <TextField multiline rows={4} fullWidth value={key} onChange={(e) => setKey(e.target.value)}
            placeholder="-----BEGIN RSA PRIVATE KEY-----..." sx={{ mt: 0.5 }} />
        </Box>
      </Stack>
      <Button variant="outlined" sx={{ mt: 2 }} disabled={!host || !user || !key}
        onClick={() => onTest({ host, port, username: user, private_key: key })}>
        Test Connection
      </Button>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function StepCloud({ sessionId, onNext }: Props) {
  const navigate = useNavigate();
  const [tab, setTab]                   = useState(0);
  const [testing, setTesting]           = useState(false);
  const [results, setResults]           = useState<Record<string, TestResult>>({});
  const [saving, setSaving]             = useState(false);
  const [savedProvider, setSavedProvider] = useState<string | null>(null);

  const provider = PROVIDERS[tab];
  const result   = results[provider] ?? null;

  async function handleTest(creds: Record<string, string>) {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/onboarding/${sessionId}/test-connection`, {
        provider, credentials: creds,
      });
      setResults((prev) => ({ ...prev, [provider]: { success: data.success, message: data.message } }));
      if (data.success) {
        // Auto-save on successful test
        setSaving(true);
        await axios.post(`${API_BASE}/onboarding/${sessionId}/credentials`, {
          provider, credentials: creds,
        });
        setSavedProvider(provider);
      }
    } catch {
      setResults((prev) => ({ ...prev, [provider]: { success: false, message: 'Connection test failed — please check your credentials.' } }));
    } finally {
      setTesting(false);
      setSaving(false);
    }
  }

  const LABELS: Record<Provider, string> = { aws: 'AWS', gcp: 'Google Cloud', azure: 'Microsoft Azure', ssh: 'Private Server' };

  return (
    <Box maxWidth={640} mx="auto" sx={{ position: 'relative' }}>
      <IconButton
        onClick={() => navigate(-1)}
        sx={{ position: 'absolute', top: 16, right: 16, color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <CloseIcon />
      </IconButton>
      <Typography variant="h5" fontWeight={700} textAlign="center" mb={0.5}>
        Connect your cloud infrastructure
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
        Shield needs read-only access to scan your environment. We never store your master credentials.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        {PROVIDERS.map((p, i) => (
          <Tab key={p} label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {LABELS[p]}
              {results[p]?.success && <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />}
            </Box>
          } />
        ))}
      </Tabs>

      {testing || saving ? (
        <Box textAlign="center" py={4}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary" mt={1}>
            {testing ? 'Testing connection…' : 'Saving credentials…'}
          </Typography>
        </Box>
      ) : (
        <>
          {tab === 0 && <AWSForm sessionId={sessionId} onConnected={() => setSavedProvider('aws')} />}
          {tab === 1 && <GCPForm   onTest={handleTest} />}
          {tab === 2 && <AzureForm onTest={handleTest} />}
          {tab === 3 && <SSHForm   onTest={handleTest} />}
          <StatusBadge result={result} />
        </>
      )}

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={!savedProvider}
          sx={{ fontWeight: 700 }}
        >
          Continue →
        </Button>
      </Box>
    </Box>
  );
}
